import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AUTOPILOT_ENABLED } from '@/lib/featureFlags';
import { getRecommendations } from '@/features/autopilot/store';

// Autopilot cursor state — the toggle and the top open recommendation that the
// guided overlay points at. Persisted per browser; default off. The overlay is
// purely a guide: it never moves the OS cursor and never executes an action.
const Ctx = createContext({ enabled: false, toggle: () => {}, top: null, dismiss: () => {}, refresh: () => {} });
export const useAutopilot = () => useContext(Ctx);

export function AutopilotProvider({ children }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem('autopilot_cursor_enabled') === '1'; } catch { return false; }
  });
  const [recs, setRecs] = useState([]);
  const [hidden, setHidden] = useState(() => new Set());

  const refresh = useCallback(async () => {
    if (!AUTOPILOT_ENABLED) return;
    try { setRecs((await getRecommendations()).recommendations || []); } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [enabled, refresh]);

  const toggle = useCallback(() => {
    setEnabled((e) => {
      const next = !e;
      try { localStorage.setItem('autopilot_cursor_enabled', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const dismiss = useCallback((id) => setHidden((h) => { const n = new Set(h); n.add(id); return n; }), []);
  const top = recs.find((r) => !hidden.has(r.id)) || null;

  return <Ctx.Provider value={{ enabled, toggle, top, dismiss, refresh }}>{children}</Ctx.Provider>;
}
